import { t } from "i18next";
import { CoreInterfaces } from "src/core/Models";
import * as Constants from "./../core/Constants";
import { fetchData, sendFile } from "./api";

export function fetchEngagementDocuments(
  engagementConfigurationId: string
): Promise<Array<CoreInterfaces.EngagementDocument>> {
  const url = Constants.APIPath.EngagementDocumentGetAll.replace(
    "{engagementConfigurationId}",
    engagementConfigurationId
  );
  return fetchData<any>(url);
}

export function getFileContent(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      const fileContent = (evt.target.result as string).split(",")[1];
      resolve(fileContent);
    };
    reader.onerror = (err) => {
      reject(err);
    };
    reader.readAsDataURL(file);
  });
}

export const handleFileUpload = (
  businessOpportunityId: string,
  engagementConfigurationId: string,
  file: any,
  documentType: Constants.EngagementDocumentType,
  maxSize: number = null,
  allowedExtensions: Array<string> = [],
  customFilename: string = null
): Promise<void> => {
  if (Array.isArray(allowedExtensions) && allowedExtensions.length) {
    const fileExtension = file.name?.split(".").pop();
    if (!allowedExtensions.includes(fileExtension.toLowerCase())) {
      return new Promise((_, reject) => {
        reject(t(`General.InvalidFileExtension`));
      });
    }
  }

  if (maxSize && file.size > maxSize) {
    return new Promise((_, reject) => {
      reject(
        t("General.EngagementUploaderMaximulAllowedFileSize", {
          size: (maxSize / 1024 / 1024).toFixed() + " MB",
        })
      );
    });
  }

  const formData = new FormData();

  if (!!customFilename) {
    formData.append("file", file, customFilename);
  } else {
    formData.append("file", file);
  }
  formData.append("DocumentType", documentType);

  return sendFile(
    Constants.APIPath.EngagementDocumentsUpload.replace(
      "{businessOpportunityId}",
      businessOpportunityId
    ).replace("{engagementConfigurationId}", engagementConfigurationId),
    Constants.APIMethod.POST,
    formData
  )
    .then(
      // eslint-disable-next-line
      (_) => {}
    )
    .catch((Ex) =>
      console.log("Exception encountered during document upload", Ex)
    );
};

export function deleteDocument(
  businessOpportunityId: string,
  engagementConfigurationId: string,
  document: CoreInterfaces.EngagementDocument
) {
  const url = Constants.APIPath.EngagementDocumentDelete.replace(
    "{businessOpportunityId}",
    businessOpportunityId
  )
    .replace("{engagementConfigurationId}", engagementConfigurationId)
    .replace("{documentId}", document.Id);
  return fetchData<any>(url, Constants.APIMethod.DELETE);
}

export function fetchEngagementLetterSignnes(
  caseId: string,
  documentId: string,
  token: string
): Promise<Array<CoreInterfaces.EngagementLetterSignee>> {
  const url = Constants.APIPath.EngagementLetterSignees.replace(
    "{caseId}",
    caseId
  ).replace("{documentId}", documentId);
  const customHeaders = [
    {
      key: Constants.DigitalSigningAuthTokenRequestHeaderKey,
      value: token,
    },
  ];

  return fetchData<any>(
    url,
    Constants.APIMethod.GET,
    null,
    {},
    false,
    customHeaders
  );
}

export function fetchSigningStatus(
  businessOpportunityId: string,
  configurationId: string,
  token: string
): Promise<boolean> {
  const url = Constants.APIPath.DigitalSigningStatusChange.replace(
    "{businessOpportunityId}",
    businessOpportunityId
  ).replace("{configurationId}", configurationId);
  const customHeaders = [
    {
      key: Constants.DigitalSigningAuthTokenRequestHeaderKey,
      value: token,
    },
  ];

  return fetchData<boolean>(
    url,
    Constants.APIMethod.GET,
    null,
    {},
    false,
    customHeaders
  );
}

export function doCancelSigningProcess(
  businessOpportunityId: string,
  engagementConfigurationId: string,
  comment: string,
  accessToken: string
): Promise<CoreInterfaces.EngagementConfigurationReadPack> {
  const url = Constants.APIPath.DigitalSigningCancel;
  const customHeaders = [
    {
      key: Constants.DigitalSigningAuthTokenRequestHeaderKey,
      value: accessToken,
    },
  ];

  const payload = {
    BusinessOpportunityId: businessOpportunityId,
    EngagementConfigurationId: engagementConfigurationId,
    Comment: comment,
  };

  return fetchData<CoreInterfaces.EngagementConfigurationReadPack>(
    url,
    Constants.APIMethod.PUT,
    payload,
    {},
    false,
    customHeaders
  );
}

export async function fetchDownloadSignedDocument(
  businessOpportunityId: string,
  engagementConfigurationId: string,
  token: string
): Promise<Blob | null> {
  const url = `${
    Constants.API_BASE_URL
  }${Constants.APIPath.DigitalSigningDownloadSignedDocument.replace(
    "{businessOpportunityId}",
    businessOpportunityId
  ).replace("{engagementConfigurationId}", engagementConfigurationId)}`;

  const headers = new Headers({
    [Constants.DigitalSigningAuthTokenRequestHeaderKey]: token,
  });

  try {
    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`
      );
    }

    // Extract filename from Content-Disposition header
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i
    );
    let fileName = match?.[1] || "downloaded_file";
    try {
      fileName = decodeURIComponent(fileName);
    } catch (e) {
      console.error(
        "Error decoding filename, using original value instead:",
        e
      );
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(blobUrl);
    return blob;
  } catch (error) {
    console.error("Error while downloading signed document:", error);
    return null;
  }
}
